import { Action, Reducer } from 'redux';
import { ProviderImportJob } from '../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { isImportJobActive, isImportJobFinished, MAX_NOTIFIED_IDS } from '../../functions/ImportJobs.Functions';
import {
  IMPORT_JOBS_FOREGROUND,
  IMPORT_JOBS_INIT,
  IMPORT_JOBS_NOTICE_DONE,
  IMPORT_JOBS_POLLING,
  IMPORT_JOBS_TRACK,
  IMPORT_JOBS_UPDATED,
  ImportJobsKnownAction,
} from '../actions/ImportJobs.Actions';

/**
 * Aviso global de las importaciones en segundo plano (spec 008). Puro: sessionStorage y timers viven en el
 * middleware (ImportJobs.Middleware) y los toasts en el componente (ImportJobsWatcher).
 */
export interface ImportJobsState {
  /** Jobs conocidos por id (activos y terminados en esta sesión). */
  jobs: Record<string, ProviderImportJob>;
  /** Jobs cuyo fin se avisa (del usuario: iniciados acá, seguidos por 409 o devueltos por `mine`). */
  notifyIds: string[];
  /** Jobs que muestra el asistente abierto: su fin no lleva toast (sí notificación si la pestaña está oculta). */
  foregroundIds: string[];
  /** Jobs ya avisados (de-dupe; se persiste en sessionStorage). */
  notifiedIds: string[];
  /** Jobs terminados pendientes de aviso, en orden. */
  pendingNotices: string[];
  /** Por Provider.id: se incrementa cuando termina un job del proveedor (la grilla abierta recarga). */
  reloadTokens: Record<string, number>;
  polling: boolean;
  initialized: boolean;
  /** Inicio de la sesión de avisos (ISO). */
  sessionSince?: string;
}

export const ImportJobsUnloadedState: ImportJobsState = {
  jobs: {},
  notifyIds: [],
  foregroundIds: [],
  notifiedIds: [],
  pendingNotices: [],
  reloadTokens: {},
  polling: false,
  initialized: false,
  sessionSince: undefined,
};

const addId = (ids: string[], id: string): string[] => (ids.includes(id) ? ids : [...ids, id]);

/** Aplica jobs nuevos: detecta los que terminaron para avisar y recargar. */
const applyJobs = (state: ImportJobsState, incoming: ProviderImportJob[], notify: boolean): ImportJobsState => {
  let jobs = state.jobs;
  let notifyIds = state.notifyIds;
  let pendingNotices = state.pendingNotices;
  let reloadTokens = state.reloadTokens;
  incoming.forEach((job) => {
    if (!job?.id) return;
    const id = String(job.id);
    const previous = jobs[id];
    jobs = { ...jobs, [id]: { ...previous, ...job, id } };
    if (notify) notifyIds = addId(notifyIds, id);
    if (!isImportJobFinished(job)) return;
    const justFinished = !!previous && isImportJobActive(previous);
    const shouldNotify = notifyIds.includes(id) && !state.notifiedIds.includes(id) && !pendingNotices.includes(id);
    if (shouldNotify) pendingNotices = [...pendingNotices, id];
    if (justFinished || shouldNotify) {
      const key = String(job.providerId);
      reloadTokens = { ...reloadTokens, [key]: (reloadTokens[key] ?? 0) + 1 };
    }
  });
  return { ...state, jobs, notifyIds, pendingNotices, reloadTokens };
};

export const ImportJobsReducer: Reducer<ImportJobsState> = (state: ImportJobsState | undefined, incomingAction: Action): ImportJobsState => {
  if (state === undefined) return ImportJobsUnloadedState;
  const action = incomingAction as ImportJobsKnownAction;
  switch (action.type) {
    case IMPORT_JOBS_INIT:
      return {
        ...state,
        initialized: true,
        sessionSince: action.sessionSince,
        notifiedIds: Array.from(new Set([...state.notifiedIds, ...action.notifiedIds.map(String)])).slice(-MAX_NOTIFIED_IDS),
      };
    case IMPORT_JOBS_TRACK:
      return applyJobs(state, [action.job], action.notify);
    case IMPORT_JOBS_UPDATED:
      return applyJobs(state, action.jobs ?? [], action.notify);
    case IMPORT_JOBS_FOREGROUND: {
      const id = String(action.jobId);
      const foregroundIds = action.foreground ? addId(state.foregroundIds, id) : state.foregroundIds.filter((current) => current !== id);
      return { ...state, foregroundIds };
    }
    case IMPORT_JOBS_NOTICE_DONE: {
      const id = String(action.jobId);
      return {
        ...state,
        pendingNotices: state.pendingNotices.filter((current) => current !== id),
        notifiedIds: addId(state.notifiedIds, id).slice(-MAX_NOTIFIED_IDS),
      };
    }
    case IMPORT_JOBS_POLLING:
      return state.polling === action.polling ? state : { ...state, polling: action.polling };
    default:
      return state;
  }
};

/** Jobs activos conocidos (para decidir si se sigue consultando). */
export const activeImportJobs = (state: ImportJobsState | undefined): ProviderImportJob[] =>
  Object.values(state?.jobs ?? {}).filter(isImportJobActive);

export default ImportJobsReducer;
